// Sound and screen: where your house hears and sees the game. A game can be played from several houses, and each one
// needs one device that plays it: a phone with its music on, or a TV following the room (tv.ts, /tv?r=CODE), which
// shows and plays everything a moment behind while the phones stay the buzzers. The speaker key at the top of every
// party phone opens this sheet, at any point in the game. A phone casts its TV (cast-send.ts); any TV with a browser
// gets there with the code. The app has the same sheet, with Cast and AirPlay of its own (app/src/game/screen.tsx).
import { esc, icon, store, t, track } from './common.ts';
import { CHROME, askTv, castContext, castDevices, castOn, castPermission, loadCastSdk, onCastEnd, wait } from './cast-send.ts';
import type { GameView } from '../core/types.ts';

export interface HouseOpts {
  get: () => GameView | null;
  room: () => string;
  hosting: boolean; // this device runs the game, so its music can't go off
  here: () => boolean; // its music is on
  musicOn: () => void;
  musicOff: () => void;
  names: () => string; // who else has the sound on, for "With them?"
  changed: () => void; // the key and the lobby's card say where the game is
}
type CastLine = 'idle' | 'searching' | 'again' | 'connecting' | 'none' | 'blocked' | 'error' | 'closed';
const LINE: Record<CastLine, () => string> = {
  idle: () => '',
  searching: () => t('Looking for your TV… if Chrome asks, choose Allow'),
  again: () => t('Ready: tap Cast to your TV again'),
  connecting: () => t('Putting the game on your TV…'),
  none: () => t('No TV found. Is it on, and on the same Wi-Fi?'),
  blocked: () => t('Chrome is blocking the search for your TV'),
  error: () => t('The TV didn\'t answer. Try again.'),
  closed: () => t('The TV closed SongBuzz. Tap Cast to your TV again.'),
};
let O: HouseOpts | null = null, box: HTMLElement | null = null, back: HTMLElement | null = null;
let line: CastLine = 'idle', busy = false, reloadAt = 0;
const tvKey = () => 'sb_tv:' + (O ? O.room() : '');
/** The TV this device put the game on, for this room ('' when its name isn't known), or null. */
export function houseTv(): string | null { return O && O.room() ? store.get(tvKey()) : null; }
function setTv(name: string | null) {
  if (name == null) { try { localStorage.removeItem(tvKey()); } catch (e) {} } else store.set(tvKey(), name);
  if (O) O.changed();
  draw();
}

export function houseInit(opts: HouseOpts) {
  O = opts;
  // the TV stopped showing it (from Chrome, from the TV, or it closed the app when told to follow): this device knows
  onCastEnd(() => {
    if (houseTv() == null) return;
    const failed = Date.now() - reloadAt < 20000;
    track('tv_follow_end', { failed });
    line = failed ? 'closed' : 'idle';
    setTv(null);
  });
}

export function openHouse() {
  if (!O || document.querySelector('.hsheet')) return;
  back = document.activeElement as HTMLElement | null;
  box = document.createElement('div');
  box.className = 'hsheet';
  box.setAttribute('role', 'dialog'); box.setAttribute('aria-modal', 'true'); box.setAttribute('aria-labelledby', 'hsH');
  box.addEventListener('click', (e) => {
    const el = e.target as Element;
    if (el.closest('[data-hsclose]')) return closeHouse();
    if (el.closest('[data-hsmusic]')) { if (O!.here()) O!.musicOff(); else { O!.musicOn(); line = line === 'closed' ? 'idle' : line; } draw(); return; }
    if (el.closest('[data-hscast]')) return void castTv();
    if (el.closest('[data-hsstop]')) return void stopTv();
  });
  document.body.appendChild(box);
  document.documentElement.classList.add('pinlocked');
  line = line === 'closed' ? line : 'idle';
  draw();
  box.querySelector<HTMLElement>('.fx')!.focus();
  track('house_sheet', { tv: houseTv() != null, here: O.here() });
}
export function closeHouse() {
  if (!box) return;
  box.remove(); box = null;
  document.documentElement.classList.remove('pinlocked');
  if (back && back.isConnected) back.focus();
}
addEventListener('keydown', (e) => { if (e.key === 'Escape') closeHouse(); });
/** The game moved on: the sheet keeps up (who has the sound on), redrawn only when what it says changes. */
let drawn = '';
export function houseUpdate() { if (box && O && drawn !== sig()) draw(); }
const sig = () => [O!.here(), O!.names(), houseTv()].join('|');

const TV_ADDR = () => `<code>${esc(location.host)}/tv</code>`;
function draw() {
  if (!box || !O) return;
  drawn = sig();
  const tv = houseTv(), here = O.here(), names = O.names(), code = esc(O.room());
  const canCast = castOn && CHROME;
  const browserWay = t('On the TV’s web browser, open {addr}, choose {join} and type {code}.', { addr: TV_ADDR(), join: `<b>${esc(t('Join another room'))}</b>`, code: `<b class="hscode">${code}</b>` });
  const music = O.hosting
    ? `<p>${esc(t('This device runs the game, so its music stays on.'))}</p>`
    : `<p>${esc(here ? t('This device is the speaker for your room. Anyone sitting with you should leave theirs off.') : names ? t('Sound is on {names}. With them? Leave this off so you don’t echo.', { names }) : t('For a house with no TV. Anyone sitting with you leaves theirs off.'))}</p>
       <button class="btn${here || tv != null ? ' ghost' : ''}" type="button" data-hsmusic>${esc(here ? t('Mute') : t('Music here'))}</button>`;
  const screen = tv != null
    ? `<h3>${esc(tv ? t('On {tv}', { tv }) : t('On your TV'))}</h3>
       <p>${esc(t('Your house sees and hears the game on the TV. Leave the phones’ sound off.'))}</p>
       ${O.hosting ? `<p class="hswarn">${esc(t('Turn this device’s volume down, or you’ll hear it twice.'))}</p>` : ''}
       <button class="btn ghost" type="button" data-hsstop>${esc(t('Stop showing it'))}</button>`
    : `<h3>${esc(t('Show it on a TV'))}</h3>
       <p>${esc(t('The game and its music on the TV in your house, a moment behind. Phones stay the buzzers.'))}</p>
       ${canCast ? `<button class="btn go" type="button" data-hscast${busy ? ' aria-busy="true"' : ''}>${icon('cast')}${esc(t('Cast to your TV'))}</button>
       <p class="hsline" role="status">${esc(LINE[line]())}</p>
       <p class="hsalt">${t('No Chromecast? {how}', { how: browserWay })}</p>` : `<p class="hsway">${browserWay}</p>`}`;
  box.innerHTML = `<div class="hk"><div class="fh"><h2 id="hsH">${esc(t('Sound and screen'))}</h2><button class="fx" type="button" data-hsclose aria-label="${esc(t('Close'))}">${icon('cross')}</button></div>
    <p class="fnote">${esc(t('In each house, one device plays the game: this one, or your TV.'))}</p>
    <div class="hsopts"><section class="hso${here ? ' on' : ''}"><i class="hsi">${icon('sound')}</i><div><h3>${esc(t('Music on this device'))}</h3>${music}</div></section>
    <section class="hso${tv != null ? ' on' : ''}"><i class="hsi">${icon('tv')}</i><div>${screen}</div></section></div>
    <button class="btn wide" type="button" data-hsclose>${esc(t('Done'))}</button></div>`;
  // keep the focus where it was through a redraw
  const f = box.querySelector<HTMLElement>(busy ? '[data-hscast]' : '.fx');
  if (f && !box.contains(document.activeElement)) f.focus();
}
function say(l: CastLine) { line = l; const el = box && box.querySelector('.hsline'); if (el) el.textContent = LINE[l](); }

// Cast: Chrome's list of TVs, then the TV is told to follow this room, until it says it does
async function castTv() {
  if (busy || !O) return;
  busy = true; draw();
  const tapAt = Date.now(), room = O.room();
  track('tv_follow_tap', {});
  try {
    if (!castContext()) {
      say('searching');
      try { await loadCastSdk(); } catch (e) { track('tv_follow', { result: 'sdk', why: (e as Error).message }); return say('error'); }
    }
    for (let i = 0; i < 12 && !castDevices(); i++) { say('searching'); await wait(250); }
    const ctx = castContext()!;
    let session = ctx.getCurrentSession(); // a TV this site is already on: that one
    if (!session) {
      // Chrome only opens its list straight after a tap
      if (Date.now() - tapAt > 4500) return say('again');
      try { await ctx.requestSession(); } catch (e) {
        const code = String((e && (e as { code?: string }).code) || e);
        if (code === 'cancel') return say('idle');
        const p = await castPermission();
        track('tv_follow', { result: 'fail', why: `${code}·perm_${p}` });
        return say(code === 'receiver_unavailable' ? (p === 'denied' ? 'blocked' : 'none') : 'error');
      }
      session = ctx.getCurrentSession();
      if (!session) return say('error');
    }
    say('connecting');
    const r = await askTv(session, { t: 'follow', room }, (d) => (d.noroom ? 'noroom' : d.following === room ? (d.reload ? 'reload' : 'on') : null));
    track('tv_follow', { result: r || 'no_answer' });
    if (r !== 'on' && r !== 'reload') return say('error');
    if (r === 'reload') reloadAt = Date.now(); // it starts again on the room's address: if that closes it, the end says so
    let name = ''; try { name = session.getCastDevice().friendlyName || ''; } catch (e) {}
    line = 'idle';
    if (O.here() && !O.hosting) O.musicOff(); // the TV is the speaker now
    setTv(name);
  } finally { busy = false; draw(); }
}
async function stopTv() {
  track('tv_follow_stop', {});
  // the session comes back by itself once the library is up again (a reload of this page)
  if (castOn && CHROME) {
    try { await loadCastSdk(); } catch (e) {}
    for (let i = 0; i < 8 && castContext() && !castContext()!.getCurrentSession(); i++) await wait(250);
    const ctx = castContext();
    if (ctx && ctx.getCurrentSession()) ctx.endCurrentSession(true);
  }
  line = 'idle';
  setTv(null);
}
